import jwtDecode from 'jwt-decode';
import { Container, DependencyContainerContext } from '@meraki-internal/react-dependency-injection';
import { AuthenticationClientConfig, AuthenticationClientRedirecter, MemoryLastCodeVerifierProvider, HTTPClientConfig, HTTPClient, APIClient, HTTPClientNetwork, HTTPClientCaches } from '@innerhive-internal/innerhive-api-client';
import { BrowserAuthenticationClientRedirecter } from './auth/BrowserAuthenticationClientRedirecter';
import { LocalStorageLastCodeVerifierProvider } from './auth/LocalStorageLastCodeVerifierProvider';
import { Auth0AuthService, AuthService, AuthStorageProvider, IAuthService, ViewerAuthService } from './auth/AuthService';
import { Logger } from './support/Logger';
import { AppPrincipal, isAnonymousViewer } from './auth/AppPrincipal';
import { LocalStorageProvider } from './support/LocalStorageProvider';
import { AsyncStorageProvider, AsyncStorageProviderConfig } from './support/AsyncStorageProvider';
import { EnvConfiguration, getConfiguration, getEnvironment } from './config/EnvConfiguration';
import { UserState } from './user/UserState';
import { PreAuthProfileService } from './user/PreAuthProfileService';
import { MixPanelServiceFacade as MixpanelService } from './metrics/MixpanelService';
import { FatalApplicationErrorState } from './FatalApplicationErrorState';
import { setSentryContext } from './support/sentry';
import { PageViewTracking } from './metrics/PageViewTracking';
import { DeviceValidator } from './support/DeviceValidator';
import { IntercomService } from './support/IntercomService';
import { isPlatform } from './utils/isPlatform';
import { isAutomatedTests } from './utils/isAutomatedTests';
import { DevSettings } from './support/DevSettings';
import { DeviceInfo } from './support/DeviceInfo';
import { AppInfo } from './support/AppInfo';
import {
    LiveUpdatesService,
    LiveUpdatesServiceLogger,
    LiveUpdatesServiceNetwork,
    LiveUpdatesListenerHistory,
    configure as configureLiveUpdates
} from '@meraki-internal/live-updates';
import { HistoryViewModel } from './support/HistoryViewModel';
import { NetworkAdapter } from './support/NetworkAdapter';
import { NativeVersionManager } from './support/forced-updates/NativeVersionManager';
import { Device } from '@capacitor/device';
import { LoadingPage } from './LoadingPage';
import { State, useSubscription } from '@meraki-internal/state';
import { FeatureFlagState } from './support/feature-flags/FeatureFlagState';
import { RevenueCatModel } from './innerhive-plus/revenue-cat/RevenueCatModel';
import { RevenueCatAPIModel } from './innerhive-plus/revenue-cat/RevenueCatAPIModel';
import { RevenueCatSDKModel } from './innerhive-plus/revenue-cat/RevenueCatSDKModel';
import { OrganizationsState } from './organizations/OrganizationsState';
import { CareMapState } from './caremap/CareMapState';
import { WelcomePageState } from './QuestionnaireWelcomePage';
import { CheckinState } from './journal/model/CheckinState';
import { AnonymousViewerSessionManager } from './auth/anonymous/AnonymousViewerSessionManager';
import { AnonymousClientStorageProvider, CompositeAnonymousHttpClient } from './auth/anonymous/CompositeAnonymousHttpClient';
import { App, AppState } from '@capacitor/app';
import { CallDetector, PhoneState } from 'capacitor-plugin-incoming-call';
import { BufferedEventBus } from './utils/BufferedEventBus';
import { DeviceStatusBar } from './support/DeviceStatusBar';
import { Clock } from './utils/Clock';
import { UAParser } from 'ua-parser-js';
import packageJson from '../package.json';

let errorSafeContainer: Container | undefined;

export const getErrorSafeContainer = () => {
    return errorSafeContainer || new Container();
};

let mountCount = 0;

export const APP_FOREGROUNDED = 'App.Foregrounded';
export const APP_BACKGROUNDED = 'App.Backgrounded';
export const APP_USER_INTERACTED = 'App.UserInteracted';
export const APP_CALL_STATE_CHANGE = 'App.CallStateChange';

const loadingPageState = new State<{showLogo: boolean}>({showLogo: false});
const SmartLoadingPage: React.FC = () => {
    useSubscription(() => loadingPageState);
    return <LoadingPage showLogo={loadingPageState.state.showLogo} />;
};
export class AppContainer extends DependencyContainerContext {
    renderLoading() {
        return <SmartLoadingPage /> as any;
    };

    /**
     * This is awaited from componentDidMount in the base class DependencyContainerContext.
     * WARNING: Because this is async, errors thrown from here are silently swallowed by React.
     * So we must catch and handle them manually, or the app will white screen.
     */
    async containerMounted(container: Container) {
        try {
            // ignore if we get a second mount - this is from react attempting to remount
            // after an error, but we have already rendered the error page at this point
            mountCount++;
            if (mountCount > 1){
                container.get(Logger).info('AppContainer tried to mount again');
                return;
            }

            errorSafeContainer = container;

            await this.configureContainer(container);
        } catch (e: any) {
            container.get(Logger).error(e);

            try {
                await container.get(MixpanelService).init();
            }
            catch (err: any){
                console.log('in mix panel init failed, with original error', e, 'and mixpanel error', err);
                // TODO: I think this is failing but I don't see how so adding logging...
                try {
                    container.get(Logger).info('failed to init mixpanel while trying to render failed app start');
                }
                catch (err2: any){
                    console.log('in logging failed', err2);
                }

            }

            FatalApplicationErrorState.singleton.throwError(e);
        }
    }

    private stopIfTestParkingLotPage = async () => {
        if (window.location.search === '?__test_parking_lot_page'){
            const el = document.createElement('h1');
            el.setAttribute('data-id', 'parked');
            el.innerHTML = 'Welcome to the parking lot';
            document.body.appendChild(el);
            await new Promise(resolve => {});
        }
    };

    async configureContainer(container: Container) {

        const log = container.get(Logger);

        // Surface progress on window for E2E diagnostics. waitForContainerReady reads this on
        // timeout to pinpoint which step is hanging — the configure pipeline is long and a hang
        // anywhere blocks the whole app from becoming ready, with no other signal of where.
        const markProgress = (label: string) => {
            (window as any).appContainerProgress = label;
            log.info(`AppContainer progress: ${label}`);
        };
        markProgress('1: starting');

        // determine if we are offline or not early, so consumers are more likely to get the right answer
        await container.get(NetworkAdapter).startListening();

        const isRunningAutomatedTests = isAutomatedTests();

        log.info('Loading environment config...');

        const deviceInfo = await Device.getInfo();
        const { identifier } = await Device.getId();
        const { name: browserName, version: browserVersion } = container.get(UAParser).getBrowser();
        const enhancedDevice: DeviceInfo = {
            ...deviceInfo,
            uuid: identifier,
            browserName,
            browserVersion,
            osVersion: deviceInfo.osVersion
        } as DeviceInfo;
        container.registerInstance(DeviceInfo, enhancedDevice);

        if (['ios', 'android'].includes(deviceInfo.platform)) {
            const appInfo = await App.getInfo();
            container.registerInstance(AppInfo, appInfo as AppInfo);
        } else {
            // on web, use package.json version
            const webAppInfo: AppInfo = {
                name: packageJson.name,
                id: packageJson.name,
                version: packageJson.version,
                build: ''
            } as AppInfo;
            container.registerInstance(AppInfo, webAppInfo);
        }

        container.registerInstance(AsyncStorageProviderConfig, {
            platform: deviceInfo.platform,
            backwardsCompatibilityWebKeys: [
                'ih:auth',
                'shown-nudges'
            ]
        } as AsyncStorageProviderConfig);

        const devSettings = container.get(DevSettings);

        const appEnv = getEnvironment(devSettings);
        const config = getConfiguration(appEnv);

        if (isRunningAutomatedTests){
            // disable tracking
            config.MIXPANEL_TOKEN = '';
            config.INTERCOM_APP_ID = '';

            // NOTE: short-pause auto-save (recordingShortPauseAutoSave) is intentionally NOT enabled
            // globally here. With the 5s test timeout on every recording, any spec that pauses and
            // takes >5s to reach its next action (common under CI load) had the recording auto-save
            // out from under it — popping the success/processing modal and breaking unrelated tests.
            // The single spec that exercises auto-save (recording-pause-resume) now opts in per-block.
        }

        container.registerInstance(EnvConfiguration, config);
        log.info('config', config);

        await this.configureNativeVersionManager(container);

        const storageProvider = container.get(LocalStorageProvider);
        storageProvider.setEnv(config.ENV === 'live' ? 'live' : 'staging');
        container.get(AsyncStorageProvider).setEnv(config.ENV === 'live' ? 'live' : 'staging');

        // get it into window.IOC so tests can get to it
        container.get(AuthStorageProvider);
        container.get(EnvConfiguration);
        container.get(Clock);

        // for LoadingPage
        const liveUpdatePath = await container.get(AsyncStorageProvider).getStringProvider('LiveUpdatesAppStartPath', {userNeutral: true}).get();
        loadingPageState.setState({showLogo: liveUpdatePath ? false : true});

        // stop here if on the test blank page
        // why here, because tests will check EnvConfiguration to ensure it is consistent
        // with what tests expect (eg are both are talking to the staging api)
        await this.stopIfTestParkingLotPage();

        // platform "capacitor" means running native on a device
        // (in which case we don't want to do any validation)
        if (!isPlatform('capacitor')) {
            this.validateDevice({ deviceValidator: container.get(DeviceValidator) });
        }

        // notify on app foreground
        App.addListener('appStateChange', ({ isActive: isForegrounding }: AppState) => {
            if (isForegrounding) {
                container.get(BufferedEventBus).emit(APP_FOREGROUNDED);
            } else {
                container.get(BufferedEventBus).emit(APP_BACKGROUNDED);
            }
        });

        // emit APP_USER_INTERACTED on every user input. Consumers handle their own dedup
        // based on their state needs (e.g., SessionReplayService checks whether a replay
        // is already active before starting a new one). This keeps Mixpanel's idle-timeout
        // case correct: when a replay ends mid-session, the next user interaction restarts
        // it; we don't depend on a foreground signal that may never fire.
        const onUserInteracted = () => {
            container.get(BufferedEventBus).emit(APP_USER_INTERACTED);
        };
        document.addEventListener('pointerdown', onUserInteracted, { passive: true });
        document.addEventListener('keydown', onUserInteracted, { passive: true });

        if (isPlatform('capacitor')) {
            // for iOS, detect call state (ie recording interrupted)
            if (isPlatform('ios')) {
                // activate the callstate plugin
                CallDetector.detectCallState({ action: 'ACTIVATE' });
                CallDetector.addListener('callStateChange', (phoneState: PhoneState) => {
                    container.get(BufferedEventBus).emit(APP_CALL_STATE_CHANGE, phoneState);
                });
            }
        }

        container.registerAlias(BrowserAuthenticationClientRedirecter, AuthenticationClientRedirecter);
        container.registerAlias(LocalStorageLastCodeVerifierProvider, MemoryLastCodeVerifierProvider);

        markProgress('2: initLiveUpdates');
        await this.initLiveUpdates(container);

        container.registerInstance(AuthenticationClientConfig, { baseURL: config.API_BASE_URL });

        // <AUTH>
        markProgress('3: configureUserOrAnonymousSession');
        await this.configureUserOrAnonymousSession(container);

        const principal = container.get(AppPrincipal);
        // </AUTH>

        storageProvider.setUserId(principal.userId);
        container.get(AsyncStorageProvider).setUserId(principal.userId);

        setSentryContext({ user: principal, env: config.ENV });

        // TODO: if revenue cat is down our app won't start, ideally we'd be more resilient
        // eg if revenue cat is down, then use the last known state, or assume not premium if there isn't one
        container.registerInstance(RevenueCatModel, new RevenueCatModel(
            container.get(RevenueCatAPIModel),
            container.get(RevenueCatSDKModel),
            container.get(AppPrincipal),
            container.get(DevSettings),
            container.get(Logger),
            {
                getDeviceInfo: () => Device.getInfo()
            },
            container.get(APIClient)
        ));
        markProgress('4: RevenueCatModel.init');
        await container.get(RevenueCatModel).init();

        markProgress('5: FeatureFlagState.init');
        await container.get(FeatureFlagState).init();

        markProgress('6: UserState.load');
        const userState = container.get(UserState);

        await userState.load();

        markProgress('7: PreAuthProfileService.mergeStoredProfileOnAppStart');
        await container.get(PreAuthProfileService).mergeStoredProfileOnAppStart();

        markProgress('8: OrganizationsState.load');
        await container.get(OrganizationsState).load();

        container.get(WelcomePageState).isNonClientOrgUser = () => container.get(OrganizationsState).getNonClientOrganizations().length > 0;

        markProgress('9: CareMapState.loadWithoutActive');
        await container.get(CareMapState).loadWithoutActive();
        container.get(CareMapState)._orgState = container.get(OrganizationsState);

        // Auto-create a blank care map for fresh signed-in users so chat (and other care-map-tied
        // surfaces) always have a context. Viewers/anonymous principals are skipped — they don't
        // have permission to create care maps and should keep working against shared ones.
        if (!isAnonymousViewer(principal)) {
            markProgress('10: CareMapState.ensureCareMapExists');
            await container.get(CareMapState).ensureCareMapExists();
        }

        markProgress('11: CheckinState.load');
        await container.get(CheckinState).load();

        // this needs to happen before initializing tracking (because we need statusbar height for metrics),
        // but invoke as late as possible to allow time for safari to set env var
        markProgress('12: DeviceStatusBar.init');
        await container.get(DeviceStatusBar).init();

        markProgress('13: trackers');
        container.get(IntercomService).init({ principal });
        await container.get(MixpanelService).init();

        container.get(PageViewTracking).start();

        markProgress('14: ready');
        (window as any).appContainerReady = true;
        markProgress('15: appContainerReady set');
    }

    private configureUserOrAnonymousSession = async (container: Container) => {
        const log = container.get(Logger);
        const anonymousSessionManager = container.get(AnonymousViewerSessionManager);
        const authService = container.get(Auth0AuthService);

        // if ?v= then this will never return as it will reload the app
        await anonymousSessionManager.init();

        log.info('Getting credentials for for user...');

        const userAccessToken = await authService.maybeGetToken();

        const hasAnonmyousSessions = anonymousSessionManager.hasSessions();

        if (userAccessToken){
            await this.configureForUserAccessToken({ access_token: userAccessToken, container });
        }
        else if (hasAnonmyousSessions){
            const session_token = anonymousSessionManager.getMostRecentSession()!;
            await this.configureForAnonymousViewSessionToken({
                session_token,
                container
            });
        }
        else {
            // waits forever
            await anonymousSessionManager.useDefaultSession();
        }
    };

    private configureForAnonymousViewSessionToken = async ({ session_token, container }: { session_token: string; container: Container;}) => {
        await this.configureAnonymousHttpClientForNewUI({ session_token, container });
    };

    private configureAnonymousHttpClientForNewUI = async ({ session_token, container }: { session_token: string; container: Container;}) => {
        const config = container.get(EnvConfiguration);
        const log = container.get(Logger);
        log.info('Initializing principal for viewer...');

        container.registerInstance(AuthService, container.get(ViewerAuthService));

        container.registerInstance(HTTPClientConfig, {
            baseURL: config.API_BASE_URL,

            // intentionally omitted, so it will throw
            // on401Callback:

            accessToken: '' // gets overrided by CompositeAnonymousHttpClient
        });

        this.configureHTTPClientCaches(container, config);

        container.registerAlias(NetworkAdapter, HTTPClientNetwork);

        const anonymousHttpClientFacade = container.get(CompositeAnonymousHttpClient);
        await anonymousHttpClientFacade.init();

        const principal: AppPrincipal = {
            tokenType: 'caremap-viewer',
            userId: anonymousHttpClientFacade.anonymousUserId,
            email: '',
            isAdmin: false
        };
        container.registerInstance(AppPrincipal, principal);
        log.info('principal', principal);

        container.registerInstance(HTTPClient, anonymousHttpClientFacade);
        const sessionToView = container.get(AnonymousViewerSessionManager).getAndExpireSessionToView();
        if (sessionToView){
            const careMapToView = anonymousHttpClientFacade.getCaremapForSession(sessionToView);
            if (careMapToView) {
                // if they aren't already going to a specific page of the care recipient (like notes)
                // then send them to care
                if (!window.location.hash.includes(careMapToView.careMapId)){
                    window.location.hash = `#/care/${careMapToView.careMapId}`;
                }
            }
        }
    };

    private configureForUserAccessToken = async ({ access_token, container }: { access_token: string; container: Container; }) => {
        const log = container.get(Logger);
        const config = container.get(EnvConfiguration);

        // If not logged in, send user to login
        log.info('Getting credentials for for user...');
        const auth0Service: IAuthService = container.get(Auth0AuthService);
        container.registerInstance(AuthService, auth0Service);

        log.info('Initializing principal for user...');
        const { userId, email, isAdmin } = jwtDecode(access_token) as any;
        const principal: AppPrincipal = {
            userId,
            email,
            isAdmin,
            tokenType: 'user'
        };
        container.registerInstance(AppPrincipal, principal);
        log.info('principal', principal);

        // Should do this as early as possible; if anything depends on HTTPClient such
        // that HTTPClientConfig is created before this is called, we'll be in a bad state
        log.info('Configuring HTTP client...');

        const httpClientConfig: HTTPClientConfig = {
            baseURL: config.API_BASE_URL,
            on401Callback: auth0Service.on401Callback,
            accessToken: access_token
        };

        container.registerInstance(HTTPClientConfig, httpClientConfig);
        if (container.get(HTTPClientConfig) !== httpClientConfig) {
            throw new Error(`HTTPClientConfig already registered! ${JSON.stringify(container.get(HTTPClientConfig))}`);
        }

        this.configureHTTPClientCaches(container, config);

        container.registerAlias(NetworkAdapter, HTTPClientNetwork);

        this.configureCompositeAnonymousHttpClientWhileInUserContext(container);
    };

    private configureCompositeAnonymousHttpClientWhileInUserContext = (container: Container) => {
        // configuring the anonymous http client so we can use it to figure out info about anonymous sessions
        // but without the normal 401 handling
        const anonymousHttpClient = new CompositeAnonymousHttpClient(
            {
                ...container.get(HTTPClientConfig),
                on401Callback: undefined
            },
            container.get(AnonymousViewerSessionManager),
            container.get(AnonymousClientStorageProvider),
            container.get(HTTPClientNetwork),
            container.get(HTTPClientCaches)
        );
        container.registerInstance(CompositeAnonymousHttpClient, anonymousHttpClient);
    };

    private configureHTTPClientCaches = (container: Container, config: EnvConfiguration) => {
        // caches is shared storage, so only safe to use on devices and local dev
        if (window.caches && (isPlatform('capacitor') || config.ENV === 'local')) {
            container.registerInstance(HTTPClientCaches, window.caches);
        }
    };

    private initLiveUpdates = async (container: Container) => {
        const config = container.get(EnvConfiguration);

        container.registerAlias(NetworkAdapter, LiveUpdatesServiceNetwork);
        container.registerAlias(Logger, LiveUpdatesServiceLogger);
        container.registerAlias(HistoryViewModel, LiveUpdatesListenerHistory);

        const logger = container.get(Logger);

        try {
            await configureLiveUpdates({
                container,
                env: config.ENV === 'live' ? 'prod' : 'staging',
                sha: process.env.VITE_APP_SHA || '(no sha)',
                isDevServer: !import.meta.env.PROD,
                LIVE_UPDATES_BASE_URL: 'https://live-updates.innerhive.com',
                onInstallingLiveUpdate: async () => {
                    logger.info('installing live update');
                    await container.get(AsyncStorageProvider).getStringProvider('LiveUpdatesAppStartPath', {userNeutral: true})
                        .set(window.location.hash);
                }
            });

            await container.get(LiveUpdatesService).init({
                disableInstallOrDownloadUpdateIfAvailable: config.ENV === 'local'
            });

            // don't do anything if we're in the middle of auth!
            if (!window.location.hash.includes('/oauth/')) {
                await container.get(LiveUpdatesService).installOrDownloadUpdateIfAvailable();
            }
        }
        catch (err: any){
            container.get(Logger).error(`failed to init live updates, app proceeded gracefully. Error: ${err.toString()}`);
        }
    };

    private configureNativeVersionManager = async (container: Container) => {
        const log = container.get(Logger);

        log.info('initializing NativeVersionManager');
        const nativeVersionManager = container.get(NativeVersionManager);
        await nativeVersionManager.init();
        await nativeVersionManager.blockOnExpired();
        nativeVersionManager.startPolling();
        log.info('initializing NativeVersionManager complete');
    };

    private validateDevice = ({ deviceValidator }: { deviceValidator: DeviceValidator; }) => {
        const validation = deviceValidator.validate();

        if (!validation.validBrowser || !validation.validDevice){
            const error:any = new Error('device is not supported');
            error.errorCode = 'device-not-allowed';
            throw error;
        }
    };

};
